import type { ServiceMetrics, MetricSeries, Service, LiveTick } from "@coremonit/types";
import { makeRng, walk } from "./rng";

const RESOLUTION_SEC = 30;
const WINDOW_SEC = 60 * 60; // 1 hour

function series(rng: () => number, name: string, unit: string, base: number, jitter: number, min = 0, max = Infinity): MetricSeries {
  const points = [];
  const n = Math.floor(WINDOW_SEC / RESOLUTION_SEC);
  let v = base;
  const now = Date.now();
  for (let i = n; i >= 0; i--) {
    v = walk(rng, v, jitter, min, max);
    points.push({ ts: new Date(now - i * RESOLUTION_SEC * 1000).toISOString(), value: +v.toFixed(2) });
  }
  return { name, unit, points };
}

export function buildMetrics(s: Service, seed = 42): ServiceMetrics {
  const rng = makeRng(seed ^ s.id.split("").reduce((a, c) => a + c.charCodeAt(0), 0));
  const errBase  = s.errorRatePct;
  const p95Base  = s.latencyP95Ms;
  const p99Base  = s.latencyP99Ms;
  return {
    serviceId: s.id,
    window: "1h",
    cpu:         series(rng, "cpu.util",      "%",       55, 12, 0, 100),
    memory:      series(rng, "mem.util",      "%",       62, 10, 0, 100),
    disk:        series(rng, "disk.util",     "%",       45,  6, 0, 100),
    network:     series(rng, "net.mbps",      "Mbps",   180, 60, 0, 4000),
    iops:        series(rng, "disk.iops",     "iops",   420, 80, 0, 20000),
    latencyP50:  series(rng, "latency.p50",   "ms",     s.latencyP50Ms, 8, 0, 5000),
    latencyP90:  series(rng, "latency.p90",   "ms",     s.latencyP50Ms * 1.6, 14, 0, 5000),
    latencyP95:  series(rng, "latency.p95",   "ms",     p95Base, 18, 0, 5000),
    latencyP99:  series(rng, "latency.p99",   "ms",     p99Base, 30, 0, 8000),
    requestRate: series(rng, "req.rate",      "rpm",    s.requestsPerMin, s.requestsPerMin * 0.15, 0),
    errorRate:   series(rng, "err.rate",      "%",      errBase, Math.max(0.1, errBase * 0.4), 0, 100),
    availability:series(rng, "availability",  "%",      s.availabilityPct, 0.05, 90, 100),
    gcPct:       series(rng, "gc.pct",        "%",      2.4, 0.8, 0, 100),
    heapMb:      series(rng, "heap.mb",       "MB",     380, 60, 0, 8000),
    threads:     series(rng, "threads",       "count",  86, 12, 0, 5000),
    connections: series(rng, "conn.active",   "count",  120, 25, 0, 10000),
    queueLength: series(rng, "queue.length",  "count",  s.tags.includes("worker") ? 4200 : 20, 30, 0),
    cacheHitRate:series(rng, "cache.hit",     "%",      93, 3, 0, 100),
  };
}

/** Emit one live tick for WS. */
export function tick(s: Service, seed: number): LiveTick {
  const rng = makeRng(seed);
  return {
    serviceId: s.id,
    ts: new Date().toISOString(),
    cpu: +walk(rng, 55, 12, 0, 100).toFixed(1),
    memory: +walk(rng, 62, 10, 0, 100).toFixed(1),
    latencyP95: Math.round(walk(rng, s.latencyP95Ms, 18, 0, 5000)),
    requestsPerSec: Math.round(walk(rng, s.requestsPerMin / 60, s.requestsPerMin / 300, 0)),
    errorsPerSec: +walk(rng, s.errorRatePct / 5, 0.2, 0).toFixed(2),
  };
}
